import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useSession } from "./session-provider";
import { _api } from "../config/environment";
import { getToken } from "../utils/appHelper";
import { toast } from "sonner";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useNewEmailStore } from "@/containers/Mail/store/new-email-store";

interface SocketContextValue {
  socket: Socket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { session, isAuthenticated } = useSession();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const navigate = useHistory();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const { connections } = useNewEmailStore();

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !session?.id) {
      return;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    // Initialize socket connection
    const socketInstance = io(_api.socketUrl, {
      auth: {
        token,
      },
      transports: ["websocket", "polling"],
    });

    socketInstance.on("connect", () => {
      setConnected(true);
    });

    socketInstance.on("disconnect", (reason) => {
      setConnected(false);
    });

    socketInstance.on("connect_error", (error) => {
      console.error("🔌 Socket connection error:", error.message);
      setConnected(false);
    });

    // Listen for mail updates
    socketInstance.on(
      "mail-update",
      (data: {
        messages: {
          from: string;
          id: string;
          subject: string;
          threadId: string;
          leadId: number;
          silent: boolean;
        }[];
      }) => {
        const [receivedMessage] = data.messages;
        const leadIdFromPath = pathnameRef.current.match(/\/leads\/detail\/(\d+)/)?.[1];

        if (
          Number(leadIdFromPath) === receivedMessage.leadId ||
          receivedMessage.silent
        ) {
          void queryClient.invalidateQueries({
            queryKey: ["mail-message", leadIdFromPath],
          });
        } else {
          if (
            connections.find(
              (connection) => connection.email === receivedMessage.from,
            )
          ) {
            return;
          }

          toast.info(`New message received from ${receivedMessage.from}`, {
            description: `Subject: ${receivedMessage.subject}`,
            action: {
              label: "View",
              onClick: () => {
                navigate.push(
                  `/leads/detail/${receivedMessage.leadId}?messageId=${receivedMessage.threadId}`,
                );
              },
            },
          });
        }
      },
    );

    // Listen for mail sync completion
    socketInstance.on(
      "mail-sync",
      (data: { 
        status: string; 
        accountEmail: string; 
        syncedCount?: number; 
        leadId?: number;
        leadName?: string;
      }) => {
        if (data.status === "completed") {

          const syncedCount = data.syncedCount || 0;
          const leadInfo = data.leadName ? ` for "${data.leadName}"` : '';
          
          if (syncedCount === 0) {
            toast.info(`No new emails found${leadInfo}`, {
              description: `Account: ${data.accountEmail}`,
            });
          } else if (syncedCount === 1) {
            toast.success(`1 email synced${leadInfo}`, {
              description: `Account: ${data.accountEmail}`,
            });
          } else {
            toast.success(`${syncedCount} emails synced${leadInfo}`, {
              description: `Account: ${data.accountEmail}`,
            });
          }
          
          // Invalidate mail-message query if viewing a lead
          const leadIdFromPath = pathnameRef.current.match(/\/leads\/detail\/(\d+)/)?.[1];
          if (leadIdFromPath || data.leadId) {
            void queryClient.invalidateQueries({
              queryKey: ["mail-message"],
            });
          }
          // Refresh connections to update sync status
          void queryClient.invalidateQueries({
            queryKey: ["gmail-connections"],
          });
        }
      },
    );

    socketInstance.on("mail-token-expired", () => {
      toast.error("Error syncing Google account", {
        description: "Your Google account connection has expired. Please disconnect and reconnect it.",
      });
      void queryClient.invalidateQueries({ queryKey: ["gmail-connections"] });
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      socketInstance.off("mail-update");
      socketInstance.off("mail-sync");
      socketInstance.off("mail-token-expired");
      socketInstance.disconnect();
    };
  }, [isAuthenticated, session?.id, queryClient, connections]);

  const value: SocketContextValue = {
    socket,
    connected,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
};

export { SocketProvider };
